import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSettings } from "../context/SettingsContext";
import { getMyOrders } from "../services/api";

export default function Orders() {
  const navigate = useNavigate();
  const settings = useSettings();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await getMyOrders();
      setOrders(data);
    } catch (err) {
      console.error("Error loading orders:", err);
      if (err.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const statusLabels = {
    pending: { label: "Menunggu", bg: "#FFF3E0", color: "#E65100" },
    processing: { label: "Diproses", bg: "#E3F2FD", color: "#1565C0" },
    completed: { label: "Selesai", bg: "#E8F5E9", color: "#2E7D32" },
    cancelled: { label: "Dibatalkan", bg: "#FFEBEE", color: "#C62828" },
  };

  const paymentStatusLabels = {
    unpaid: { label: "Belum Bayar", bg: "#FFF3E0", color: "#E65100" },
    paid: { label: "Sudah Bayar", bg: "#E8F5E9", color: "#2E7D32" },
    refunded: { label: "Refund", bg: "#F3E5F5", color: "#7B1FA2" },
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const pageTitle = `Pesanan Saya - ${settings.site_name || "HandyCraft Bali"}`;

  return (
    <div className="container" style={{ paddingTop: "40px", paddingBottom: "80px" }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Lihat riwayat dan status pesanan Anda di HandyCraft Bali." />
      </Helmet>

      <div className="section-header" style={{ textAlign: "left", marginBottom: "30px" }}>
        <p>Riwayat Belanja</p>
        <h2 className="section-title">Pesanan Saya</h2>
      </div>

      {/* Back to Profile */}
      <div style={{ marginBottom: "20px" }}>
        <Link
          to="/profile"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            color: "var(--secondary)",
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          ← Kembali ke Profil
        </Link>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "80px 0" }}>
          <div style={{ fontSize: "2rem", marginBottom: "15px" }}>⏳</div>
          <p style={{ color: "#888" }}>Memuat riwayat pesanan...</p>
        </div>
      ) : orders.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "80px 40px",
            background: "white",
            borderRadius: "24px",
            border: "1px solid var(--border)",
          }}
        >
          <div style={{ fontSize: "4rem", marginBottom: "20px" }}>📦</div>
          <h3 style={{ color: "var(--secondary)", marginBottom: "10px" }}>
            Belum Ada Pesanan
          </h3>
          <p style={{ color: "#888", marginBottom: "25px" }}>
            Anda belum melakukan pemesanan. Yuk, mulai berbelanja produk kerajinan Bali!
          </p>
          <Link to="/products" className="btn btn-primary">
            🛍️ Jelajahi Produk
          </Link>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {orders.map((order) => (
            <div
              key={order.id}
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "25px",
                border: "1px solid var(--border)",
                boxShadow: "var(--shadow)",
              }}
            >
              {/* Order Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "15px",
                  marginBottom: "20px",
                  paddingBottom: "15px",
                  borderBottom: "1px solid #f5f5f5",
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "var(--secondary)",
                      marginBottom: "5px",
                    }}
                  >
                    Order #{order.id}
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#888" }}>
                    {formatDate(order.created_at)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <span
                    style={{
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      background: statusLabels[order.status]?.bg || "#eee",
                      color: statusLabels[order.status]?.color || "#666",
                    }}
                  >
                    {statusLabels[order.status]?.label || order.status}
                  </span>
                  {order.payment && (
                    <span
                      style={{
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        background: paymentStatusLabels[order.payment.status]?.bg || "#eee",
                        color: paymentStatusLabels[order.payment.status]?.color || "#666",
                      }}
                    >
                      {paymentStatusLabels[order.payment.status]?.label || order.payment.status}
                    </span>
                  )}
                </div>
              </div>

              {/* Order Items Preview */}
              <div style={{ marginBottom: "20px" }}>
                {order.items?.slice(0, 2).map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      gap: "15px",
                      padding: "12px 0",
                      borderBottom: idx < order.items.length - 1 ? "1px solid #f5f5f5" : "none",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "10px",
                        overflow: "hidden",
                        background: "#f5f5f5",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={
                          item.product?.image
                            ? item.product.image.startsWith("http")
                              ? item.product.image
                              : `/storage/${item.product.image}`
                            : "/assets/branding/thumbnail.png"
                        }
                        alt={item.product?.name || "Produk"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                        {item.product?.name || "Produk Tidak Diketahui"}
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "#888" }}>
                        {item.qty} x Rp {parseFloat(item.price).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ fontWeight: 600, color: "var(--secondary)" }}>
                      Rp {(item.qty * parseFloat(item.price)).toLocaleString()}
                    </div>
                  </div>
                ))}
                {order.items?.length > 2 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "10px",
                      color: "#888",
                      fontSize: "0.9rem",
                    }}
                  >
                    +{order.items.length - 2} produk lainnya
                  </div>
                )}
              </div>

              {/* Order Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "15px",
                  paddingTop: "15px",
                  borderTop: "1px solid #f5f5f5",
                }}
              >
                <div>
                  <div style={{ fontSize: "0.85rem", color: "#888", marginBottom: "4px" }}>
                    Total Pembayaran
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "1.3rem",
                      color: "var(--secondary)",
                    }}
                  >
                    Rp {parseFloat(order.total_price).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedOrder(order)}
                  style={{
                    padding: "10px 20px",
                    background: "var(--bg)",
                    borderRadius: "10px",
                    border: "none",
                    color: "var(--secondary)",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  Lihat Detail
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            style={{
              background: "white",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "90vh",
              borderRadius: "24px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "25px",
                borderBottom: "1px solid #f5f5f5",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ color: "var(--secondary)", margin: 0 }}>
                Detail Pesanan #{selectedOrder.id}
              </h3>
              <button
                onClick={() => setSelectedOrder(null)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "#888",
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: "25px", overflowY: "auto" }}>
              {/* Info Section */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "20px",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    padding: "20px",
                    background: "var(--bg)",
                    borderRadius: "16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#888",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      marginBottom: "8px",
                    }}
                  >
                    Status Pesanan
                  </div>
                  <span
                    style={{
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      background: statusLabels[selectedOrder.status]?.bg || "#eee",
                      color: statusLabels[selectedOrder.status]?.color || "#666",
                    }}
                  >
                    {statusLabels[selectedOrder.status]?.label || selectedOrder.status}
                  </span>
                </div>

                <div
                  style={{
                    padding: "20px",
                    background: "var(--bg)",
                    borderRadius: "16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#888",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      marginBottom: "8px",
                    }}
                  >
                    Status Pembayaran
                  </div>
                  <span
                    style={{
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      background:
                        paymentStatusLabels[selectedOrder.payment?.status]?.bg || "#eee",
                      color:
                        paymentStatusLabels[selectedOrder.payment?.status]?.color || "#666",
                    }}
                  >
                    {paymentStatusLabels[selectedOrder.payment?.status]?.label ||
                      selectedOrder.payment?.status ||
                      "-"}
                  </span>
                </div>
              </div>

              {/* Customer Info */}
              <div
                style={{
                  padding: "20px",
                  background: "var(--bg)",
                  borderRadius: "16px",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    marginBottom: "15px",
                  }}
                >
                  Informasi Pengiriman
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <strong>{selectedOrder.customer_name}</strong>
                </div>
                <div style={{ color: "#666", marginBottom: "8px" }}>
                  {selectedOrder.phone}
                </div>
                <div style={{ color: "#666", fontSize: "0.9rem" }}>
                  {selectedOrder.address}
                </div>
              </div>

              {/* Items List */}
              <div style={{ marginBottom: "25px" }}>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    marginBottom: "15px",
                  }}
                >
                  Daftar Produk
                </div>
                {selectedOrder.items?.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      gap: "15px",
                      padding: "15px 0",
                      borderBottom:
                        idx < selectedOrder.items.length - 1 ? "1px solid #f5f5f5" : "none",
                    }}
                  >
                    <div
                      style={{
                        width: "70px",
                        height: "70px",
                        borderRadius: "10px",
                        overflow: "hidden",
                        background: "#f5f5f5",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={
                          item.product?.image
                            ? item.product.image.startsWith("http")
                              ? item.product.image
                              : `/storage/${item.product.image}`
                            : "/assets/branding/thumbnail.png"
                        }
                        alt={item.product?.name || "Produk"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                        {item.product?.name || "Produk Tidak Diketahui"}
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "#888" }}>
                        {item.qty} x Rp {parseFloat(item.price).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ fontWeight: 600, color: "var(--secondary)" }}>
                      Rp {(item.qty * parseFloat(item.price)).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div
                style={{
                  padding: "20px",
                  background: "var(--secondary)",
                  borderRadius: "16px",
                  color: "white",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Total Pembayaran</span>
                  <span style={{ fontWeight: 700, fontSize: "1.5rem" }}>
                    Rp {parseFloat(selectedOrder.total_price).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "20px 25px",
                borderTop: "1px solid #f5f5f5",
                display: "flex",
                gap: "15px",
              }}
            >
              {selectedOrder.payment?.status === "unpaid" && (
                <a
                  href={`https://wa.me/${settings.whatsapp_number || "6282111307477"}?text=Halo%20HandyCraft%2C%20saya%20ingin%20konfirmasi%20pembayaran%20untuk%20Order%20%23${selectedOrder.id}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    flex: 1,
                    padding: "15px",
                    background: "#25D366",
                    borderRadius: "12px",
                    color: "white",
                    fontWeight: 600,
                    textDecoration: "none",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  💬 Konfirmasi Pembayaran
                </a>
              )}
              <button
                onClick={() => setSelectedOrder(null)}
                style={{
                  flex: selectedOrder.payment?.status === "unpaid" ? 0 : 1,
                  padding: "15px 30px",
                  background: "var(--bg)",
                  borderRadius: "12px",
                  border: "none",
                  color: "var(--secondary)",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
